import React, { useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, ScaleIcon, FilterIcon, Leaf, ClipboardListIcon, ChevronRightIcon, AlertCircleIcon, ScanLineIcon, XCircleIcon, PrinterIcon, InfoIcon, WarehouseIcon, SendIcon, CheckIcon, MapPinIcon } from 'lucide-react';
interface SpiceWeighingProps {
  onBackToOverview?: () => void;
}
export interface SpiceItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  orderId: string;
  orderName: string;
  status: 'pending' | 'in-progress' | 'completed';
  notes: string[];
}
export interface SpiceOrder {
  id: string;
  orderId: string;
  recipeName: string;
  items: SpiceItem[];
  status: 'pending' | 'in-progress' | 'completed';
  deadline: Date;
}
export function SpiceWeighing({
  onBackToOverview
}: SpiceWeighingProps) {
  const [view, setView] = useState<'list' | 'instructions' | 'preparation' | 'weighing' | 'detail' | 'remainingInventory'>('list');
  const [selectedOrder, setSelectedOrder] = useState<SpiceOrder | null>(null);
  const [selectedItem, setSelectedItem] = useState<SpiceItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'in-progress' | 'completed'>('all');
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [scannedItems, setScannedItems] = useState<string[]>([]);
  const [scannedCode, setScannedCode] = useState('');
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isWeighing, setIsWeighing] = useState(false);
  const [weight, setWeight] = useState<number | null>(null);
  const [isWithinTolerance, setIsWithinTolerance] = useState<boolean | null>(null);
  const [currentWeighingIndex, setCurrentWeighingIndex] = useState(0);
  const [weighingCompleted, setWeighingCompleted] = useState<string[]>([]);
  const [showPrintingPopup, setShowPrintingPopup] = useState(false);
  const [remainingSpices, setRemainingSpices] = useState<{
    id: string;
    name: string;
    remaining: number;
    unit: string;
  }[]>([]);
  const [isSendingToWarehouse, setIsSendingToWarehouse] = useState(false);
  const [warehouseTransferComplete, setWarehouseTransferComplete] = useState(false);
  // Beispieldaten für Gewürzverwiegungsaufträge
  const [spiceOrders, setSpiceOrders] = useState<SpiceOrder[]>([{
    id: 'SPICE001',
    orderId: '1001',
    recipeName: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(new Date().setHours(10, 0, 0, 0)),
    items: [{
      id: 'S001',
      name: 'Safran',
      quantity: 0.05,
      unit: 'kg',
      orderId: '1001',
      orderName: 'Gustoria Paella',
      status: 'pending',
      notes: ['Allergene: Keine', 'Lichtgeschützt lagern', 'Temperatur unter 20°C', 'Charge: LOT-2023-45A', 'Herkunft: Spanien']
    }, {
      id: 'S002',
      name: 'Paprika geräuchert',
      quantity: 0.25,
      unit: 'kg',
      orderId: '1001',
      orderName: 'Gustoria Paella',
      status: 'pending',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-32B', 'Herkunft: Spanien']
    }, {
      id: 'S003',
      name: 'Kurkuma',
      quantity: 0.15,
      unit: 'kg',
      orderId: '1001',
      orderName: 'Gustoria Paella',
      status: 'pending',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-28C', 'Herkunft: Indien']
    }]
  }, {
    id: 'SPICE002',
    orderId: '1002',
    recipeName: 'Gustoria Risotto',
    status: 'pending',
    deadline: new Date(new Date().setHours(13, 0, 0, 0)),
    items: [{
      id: 'S004',
      name: 'Basilikum getrocknet',
      quantity: 0.15,
      unit: 'kg',
      orderId: '1002',
      orderName: 'Gustoria Risotto',
      status: 'pending',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-28C', 'Herkunft: Italien']
    }, {
      id: 'S005',
      name: 'Oregano',
      quantity: 0.1,
      unit: 'kg',
      orderId: '1002',
      orderName: 'Gustoria Risotto',
      status: 'pending',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-30D', 'Herkunft: Italien']
    }, {
      id: 'S006',
      name: 'Schwarzer Pfeffer',
      quantity: 0.08,
      unit: 'kg',
      orderId: '1002',
      orderName: 'Gustoria Risotto',
      status: 'pending',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-35E', 'Herkunft: Vietnam']
    }]
  }]);
  // Gefilterte Aufträge basierend auf Suchbegriff und Status
  const filteredOrders = spiceOrders.filter(order => {
    const matchesSearch = order.recipeName.toLowerCase().includes(searchTerm.toLowerCase()) || order.orderId.includes(searchTerm) || order.items.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.id.includes(searchTerm));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const handleOrderSelect = (order: SpiceOrder) => {
    setSelectedOrder(order);
    setView('instructions');
    setScannedItems([]);
    setWeighingCompleted([]);
    setCurrentWeighingIndex(0);
  };
  const handleBackToList = () => {
    setView('list');
    setSelectedOrder(null);
    setSelectedItem(null);
    setSelectedNotes([]);
    setScannedItems([]);
    setWeighingCompleted([]);
  };
  const handleItemSelect = (item: SpiceItem) => {
    setSelectedItem(item);
    setSelectedNotes([]);
  };
  const handleNoteToggle = (note: string) => {
    setSelectedNotes(prevNotes => {
      if (prevNotes.includes(note)) {
        return prevNotes.filter(n => n !== note);
      } else {
        return [...prevNotes, note];
      }
    });
  };
  const handleWeighingComplete = (itemId: string) => {
    if (!selectedOrder) return;
    // Update the status for the completed item
    const updatedOrders = spiceOrders.map(order => {
      if (order.id === selectedOrder.id) {
        const updatedItems = order.items.map(item => {
          if (item.id === itemId) {
            return {
              ...item,
              status: 'completed' as const
            };
          }
          return item;
        });
        // Check if all items are completed
        const allCompleted = updatedItems.every(item => item.status === 'completed');
        return {
          ...order,
          items: updatedItems,
          status: allCompleted ? 'completed' as const : 'in-progress' as const
        };
      }
      return order;
    });
    setSpiceOrders(updatedOrders);
    // Update the selected order
    const updatedOrder = updatedOrders.find(o => o.id === selectedOrder.id);
    if (updatedOrder) {
      setSelectedOrder(updatedOrder);
    }
    // Add to completed items
    setWeighingCompleted(prev => [...prev, itemId]);
    // Move to next item
    if (selectedOrder && currentWeighingIndex < selectedOrder.items.length - 1) {
      setCurrentWeighingIndex(prev => prev + 1);
      setWeight(null);
      setIsWithinTolerance(null);
    } else {
      // All items completed - calculate remaining spices
      if (selectedOrder) {
        const remainingSpiceList = selectedOrder.items.map(item => {
          // Calculate a random remaining amount between 10% and 30% of the original quantity
          const remainingAmount = parseFloat((item.quantity * (0.1 + Math.random() * 0.2)).toFixed(3));
          return {
            id: item.id,
            name: item.name,
            remaining: remainingAmount,
            unit: item.unit
          };
        });
        setRemainingSpices(remainingSpiceList);
        setView('remainingInventory');
      }
    }
  };
  const handleSendToWarehouse = () => {
    setIsSendingToWarehouse(true);
    // Simulate sending to warehouse
    setTimeout(() => {
      setIsSendingToWarehouse(false);
      setWarehouseTransferComplete(true);
      // After a delay, go back to the list view
      setTimeout(() => {
        setView('list');
        setSelectedOrder(null);
        setWarehouseTransferComplete(false);
      }, 3000);
    }, 2000);
  };
  const handleInstructionsComplete = () => {
    setView('preparation');
  };
  const handleScan = () => {
    if (!scannedCode || !selectedOrder) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simuliere den Scanvorgang mit einer kurzen Verzögerung
    setTimeout(() => {
      // Prüfen, ob der gescannte Code zu einem der Gewürze passt
      const matchingItem = selectedOrder.items.find(item => scannedCode.includes(item.id) || scannedCode.toLowerCase().includes(item.name.toLowerCase()));
      if (matchingItem && !scannedItems.includes(matchingItem.id)) {
        setScanSuccess(true);
        setErrorMessage('');
        setScannedItems(prev => [...prev, matchingItem.id]);
      } else if (scannedItems.includes(matchingItem?.id || '')) {
        setScanSuccess(false);
        setErrorMessage('Dieses Gewürz wurde bereits gescannt!');
      } else {
        setScanSuccess(false);
        setErrorMessage('Der gescannte Code gehört zu keinem benötigten Gewürz.');
      }
      setScannedCode('');
      setIsScanning(false);
    }, 1000);
  };
  const handleAllItemsScanned = () => {
    setView('weighing');
  };
  const startWeighing = () => {
    if (!selectedOrder) return;
    setIsWeighing(true);
    // Simuliere den Wiegeprozess
    setTimeout(() => {
      const currentItem = selectedOrder.items[currentWeighingIndex];
      const tolerance = currentItem.quantity * 0.05; // 5% Toleranz
      // Zufälliges Gewicht innerhalb oder außerhalb der Toleranz
      const randomWeight = currentItem.quantity + (Math.random() * (tolerance * 1.2) - tolerance * 0.6);
      setWeight(parseFloat(randomWeight.toFixed(3)));
      setIsWeighing(false);
      // Prüfe, ob innerhalb der Toleranz
      const withinTolerance = Math.abs(randomWeight - currentItem.quantity) <= tolerance;
      setIsWithinTolerance(withinTolerance);
    }, 2000);
  };
  // Vorbereitungshinweise für die Gewürzverwiegung
  const spiceInstructions = [{
    id: 'instr-1',
    title: 'Arbeitsplatz vorbereiten',
    description: 'Stellen Sie sicher, dass der Arbeitsplatz sauber und desinfiziert ist. Bereiten Sie die Waage vor und kalibrieren Sie sie bei Bedarf.',
    icon: <CheckCircleIcon size={24} className="text-blue-500" data-id="element-1610" />
  }, {
    id: 'instr-2',
    title: 'Persönliche Schutzausrüstung anlegen',
    description: 'Tragen Sie Handschuhe, Haarnetz und Mundschutz, um Kontamination zu vermeiden. Stellen Sie sicher, dass Ihre Hände gründlich gewaschen und desinfiziert sind.',
    icon: <CheckCircleIcon size={24} className="text-blue-500" data-id="element-1611" />
  }, {
    id: 'instr-3',
    title: 'Dokumentation vorbereiten',
    description: 'Legen Sie die Dokumentationsunterlagen bereit. Alle Verwiegungen müssen protokolliert werden, einschließlich Chargen-Nummern und Mengen.',
    icon: <ClipboardListIcon size={24} className="text-blue-500" data-id="element-1612" />
  }, {
    id: 'instr-4',
    title: 'Behälter vorbereiten',
    description: 'Stellen Sie saubere, trockene Behälter für die verwogenen Gewürze bereit. Beschriften Sie die Behälter mit den entsprechenden Informationen.',
    icon: <PackageIcon size={24} className="text-blue-500" data-id="element-1613" />
  }, {
    id: 'instr-5',
    title: 'Lagerhinweise beachten',
    description: 'Beachten Sie die speziellen Lagerhinweise für jedes Gewürz. Einige Gewürze müssen lichtgeschützt oder bei bestimmten Temperaturen gelagert werden.',
    icon: <AlertCircleIcon size={24} className="text-blue-500" data-id="element-1614" />
  }];
  // State für bestätigte Anweisungen
  const [confirmedInstructions, setConfirmedInstructions] = useState<string[]>([]);
  // Funktion zum Umschalten der Bestätigung einer Anweisung
  const toggleInstructionConfirmation = (id: string) => {
    if (confirmedInstructions.includes(id)) {
      setConfirmedInstructions(confirmedInstructions.filter(item => item !== id));
    } else {
      setConfirmedInstructions([...confirmedInstructions, id]);
    }
  };
  // Prüfen, ob alle Anweisungen bestätigt wurden
  const allInstructionsConfirmed = confirmedInstructions.length === spiceInstructions.length;
  // Prüfen, ob alle Gewürze gescannt wurden
  const allItemsScanned = selectedOrder ? selectedOrder.items.every(item => scannedItems.includes(item.id)) : false;
  const handleConfirmWeighing = () => {
    setShowPrintingPopup(true);
    // After a short delay, complete the weighing process
    setTimeout(() => {
      setShowPrintingPopup(false);
      handleWeighingComplete(selectedOrder.items[currentWeighingIndex].id);
    }, 2500);
  };
  return <div className="container mx-auto" data-id="element-1615">
      {/* Header */}
      <div className="mb-8" data-id="element-1616">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-1617">
          Gewürzverwiegung
        </h2>
        <div className="flex items-center justify-between" data-id="element-1618">
          <p className="text-gray-600" data-id="element-1619">
            Exakte Dosierung und Verwiegung von Gewürzen
          </p>
          {onBackToOverview}
        </div>
      </div>

      {/* Auftragsübersicht */}
      {view === 'list' && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1620">
          {/* Suchleiste */}
          <div className="flex flex-col md:flex-row gap-3 mb-6" data-id="element-1621">
            <div className="relative flex-grow" data-id="element-1622">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1623">
                <SearchIcon size={18} className="text-gray-400" data-id="element-1624" />
              </div>
              <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Rezept oder Gewürz suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1625" />
            </div>
            <div className="flex items-center" data-id="element-1626">
              <FilterIcon size={18} className="text-gray-500 mr-2" data-id="element-1627" />
              <select className="block w-full p-2 border border-gray-300 rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} data-id="element-1628">
                <option value="all" data-id="element-1629">Alle Status</option>
                <option value="pending" data-id="element-1630">Ausstehend</option>
                <option value="in-progress" data-id="element-1631">In Bearbeitung</option>
                <option value="completed" data-id="element-1632">Abgeschlossen</option>
              </select>
            </div>
          </div>
          {/* Auftragsliste */}
          <div className="space-y-4" data-id="element-1633">
            {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${order.status === 'completed' ? 'bg-green-50 border-green-200' : order.status === 'in-progress' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} onClick={() => handleOrderSelect(order)} data-id="element-1634">
                  <div className="flex justify-between items-start" data-id="element-1635">
                    <div data-id="element-1636">
                      <h4 className="font-medium text-gray-900" data-id="element-1637">
                        {order.recipeName}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-1638">
                        Auftrags-Nr: #{order.orderId}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1639">
                        Bereitstellung bis:{' '}
                        {order.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                        Uhr
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-1640">
                      {order.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1641">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-1642" />
                          Abgeschlossen
                        </span> : order.status === 'in-progress' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1643">
                          <PackageIcon size={12} className="mr-1" data-id="element-1644" />
                          In Bearbeitung
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1645">
                          <ScaleIcon size={12} className="mr-1" data-id="element-1646" />
                          Ausstehend
                        </span>}
                    </div>
                  </div>
                  <div className="mt-3" data-id="element-1647">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-1648">
                      Fortschritt:{' '}
                      {order.items.filter(m => m.status === 'completed').length}{' '}
                      von {order.items.length} Gewürzen verwogen
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1649">
                      <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
                width: `${order.items.filter(m => m.status === 'completed').length / order.items.length * 100}%`
              }} data-id="element-1650"></div>
                    </div>
                  </div>
                  {/* Gewürze anzeigen */}
                  <div className="mt-3 flex flex-wrap gap-1" data-id="element-1651">
                    {order.items.map(item => <span key={item.id} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ${item.status === 'completed' ? 'opacity-60' : ''}`} data-id="element-1652">
                        <Leaf size={12} className="mr-1" data-id="element-1653" />
                        {item.name}
                      </span>)}
                  </div>
                </div>) : <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1654">
                <ScaleIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-1655" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-1656">
                  Keine Verwiegungsaufträge
                </h3>
                <p className="text-gray-500" data-id="element-1657">
                  Für heute sind keine Gewürzverwiegungen geplant.
                </p>
              </div>}
          </div>
        </div>}

      {/* Hinweise vor der Produktion */}
      {view === 'instructions' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1658">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-1659">
            <ArrowLeftIcon size={18} data-id="element-1660" />
            <span data-id="element-1661">Zurück zur Übersicht</span>
          </button>
          <div className="bg-white border-2 border-blue-200 rounded-xl p-8" data-id="element-1662">
            <h3 className="text-xl font-bold text-blue-800 mb-2 text-center" data-id="element-1663">
              Hinweise vor der Gewürzverwiegung
            </h3>
            <p className="text-center text-gray-600 mb-6" data-id="element-1664">
              Bitte bestätigen Sie jeden Punkt durch Anklicken
            </p>
            <div className="mb-4 text-center" data-id="element-1665">
              <span className="font-medium text-blue-800" data-id="element-1666">
                {confirmedInstructions.length} von {spiceInstructions.length}{' '}
                Punkte bestätigt
              </span>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2" data-id="element-1667">
                <div className="h-2.5 rounded-full bg-blue-500" style={{
              width: `${confirmedInstructions.length / spiceInstructions.length * 100}%`
            }} data-id="element-1668"></div>
              </div>
            </div>
            <div className="space-y-4 max-w-3xl mx-auto" data-id="element-1669">
              {spiceInstructions.map(instruction => {
            const isConfirmed = confirmedInstructions.includes(instruction.id);
            return <div key={instruction.id} className={`flex p-4 border rounded-md cursor-pointer transition-colors ${isConfirmed ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-blue-50 hover:border-blue-300'}`} onClick={() => toggleInstructionConfirmation(instruction.id)} data-id="element-1670">
                    <div className="mr-4 flex-shrink-0 mt-1" data-id="element-1671">
                      {instruction.icon}
                    </div>
                    <div className="flex-1" data-id="element-1672">
                      <div className="flex items-center justify-between" data-id="element-1673">
                        <p className="font-medium text-lg" data-id="element-1674">
                          {instruction.title}
                        </p>
                        {isConfirmed && <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center" data-id="element-1675">
                            <CheckCircleIcon size={12} className="mr-1" data-id="element-1676" />
                            Bestätigt
                          </span>}
                      </div>
                      <p className="text-gray-600" data-id="element-1677">{instruction.description}</p>
                    </div>
                  </div>;
          })}
            </div>
            <div className="mt-8 text-center" data-id="element-1678">
              <button onClick={handleInstructionsComplete} disabled={!allInstructionsConfirmed} className={`px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors ${!allInstructionsConfirmed ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1679">
                Verstanden - Gewürze bereitstellen
              </button>
              {!allInstructionsConfirmed && <p className="text-sm text-gray-500 mt-2" data-id="element-1680">
                  Bitte bestätigen Sie alle Hinweise, um fortzufahren
                </p>}
            </div>
          </div>
        </div>}

      {/* Gewürze bereitstellen und scannen */}
      {view === 'preparation' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1681">
          <button onClick={() => setView('instructions')} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-1682">
            <ArrowLeftIcon size={18} data-id="element-1683" />
            <span data-id="element-1684">Zurück zu den Hinweisen</span>
          </button>
          <div className="mb-6" data-id="element-1685">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1686">
              Gewürze bereitstellen für {selectedOrder.recipeName}
            </h3>
            <p className="text-gray-600" data-id="element-1687">
              Bitte holen Sie die folgenden Gewürze aus dem Lager und scannen
              Sie jedes Gewürz.
            </p>
          </div>
          {/* Scan-Bereich */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1688">
            <h4 className="font-medium text-gray-800 mb-4 flex items-center" data-id="element-1689">
              <ScanLineIcon size={20} className="mr-2" data-id="element-1690" />
              Gewürze scannen
            </h4>
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-1691">
              <div className="relative flex-grow" data-id="element-1692">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1693">
                  <ScanLineIcon size={18} className="text-gray-400" data-id="element-1694" />
                </div>
                <input type="text" className={`pl-10 pr-4 py-2 w-full border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md`} placeholder="Barcode scannen..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} data-id="element-1695" />
              </div>
              <button onClick={handleScan} disabled={isScanning} className={`py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1696">
                {isScanning ? 'Scanne...' : 'Scannen'}
              </button>
            </div>
            {scanSuccess === true && <div className="mb-4 flex items-center text-green-600 text-sm" data-id="element-1697">
                <CheckCircleIcon size={16} className="mr-1" data-id="element-1698" />
                <span data-id="element-1699">Gewürz erfolgreich gescannt!</span>
              </div>}
            {scanSuccess === false && <div className="mb-4 flex items-center text-red-600 text-sm" data-id="element-1700">
                <XCircleIcon size={16} className="mr-1" data-id="element-1701" />
                <span data-id="element-1702">{errorMessage}</span>
              </div>}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1703">
              <h5 className="font-medium text-gray-800 mb-3" data-id="element-1704">
                Gewürz-Checkliste:
              </h5>
              <div className="space-y-2" data-id="element-1705">
                {selectedOrder.items.map(item => {
              const isScanned = scannedItems.includes(item.id);
              return <div key={item.id} className={`flex items-center p-3 rounded-lg border ${isScanned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-1706">
                      <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${isScanned ? 'bg-green-500 text-white' : 'border border-gray-400'}`} data-id="element-1707">
                        {isScanned && <CheckCircleIcon size={14} data-id="element-1708" />}
                      </div>
                      <div className="flex-1" data-id="element-1709">
                        <div className="font-medium" data-id="element-1710">{item.name}</div>
                        <div className="text-sm text-gray-500" data-id="element-1711">
                          {item.quantity} {item.unit} • Artikel-Nr: {item.id}
                        </div>
                        <div className="text-sm text-blue-600 mt-1" data-id="element-1712">
                          <span className="flex items-center" data-id="element-1713">
                            <MapPinIcon size={12} className="mr-1" data-id="element-1714" />
                            Lagerplatz: L{item.id.slice(-3)}
                          </span>
                        </div>
                      </div>
                      <div data-id="element-1715">
                        {isScanned ? <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-1716">
                            Bereitgestellt
                          </span> : <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full" data-id="element-1717">
                            Ausstehend
                          </span>}
                      </div>
                    </div>;
            })}
              </div>
            </div>
          </div>
          <div className="text-center" data-id="element-1718">
            <button onClick={handleAllItemsScanned} disabled={!allItemsScanned} className={`px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium ${!allItemsScanned ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1719">
              Weiter zur Verwiegung
            </button>
            {!allItemsScanned && <p className="text-sm text-gray-500 mt-2" data-id="element-1720">
                Bitte scannen Sie alle benötigten Gewürze, um fortzufahren
              </p>}
          </div>
        </div>}

      {/* Verwiegungsprozess */}
      {view === 'weighing' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1721">
          <button onClick={() => setView('preparation')} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-1722">
            <ArrowLeftIcon size={18} data-id="element-1723" />
            <span data-id="element-1724">Zurück zur Vorbereitung</span>
          </button>
          <div className="mb-6" data-id="element-1725">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1726">
              Gewürzverwiegung für {selectedOrder.recipeName}
            </h3>
            <p className="text-gray-600" data-id="element-1727">
              Verwiegen Sie die Gewürze gemäß den angegebenen Mengen.
            </p>
          </div>
          {/* Aktuelles Gewürz verwiegen */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 mb-6" data-id="element-1728">
            <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-1729">
              {/* Gewürzinformationen */}
              <div className="flex-1 w-full" data-id="element-1730">
                <div className="flex items-center mb-4" data-id="element-1731">
                  <div className="p-3 bg-blue-200 rounded-full mr-4" data-id="element-1732">
                    <Leaf size={24} className="text-blue-700" data-id="element-1733" />
                  </div>
                  <div data-id="element-1734">
                    <h4 className="text-xl font-bold text-gray-800" data-id="element-1735">
                      {selectedOrder.items[currentWeighingIndex].name}
                    </h4>
                    <p className="text-gray-600" data-id="element-1736">
                      Artikel-Nr: {selectedOrder.items[currentWeighingIndex].id}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1737">
                  <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1738">
                    <h5 className="font-medium text-gray-800 mb-2" data-id="element-1739">
                      Sollmenge:
                    </h5>
                    <div className="text-3xl font-bold text-gray-800" data-id="element-1740">
                      {selectedOrder.items[currentWeighingIndex].quantity}{' '}
                      {selectedOrder.items[currentWeighingIndex].unit}
                    </div>
                    <div className="text-sm text-gray-500 mt-1" data-id="element-1741">
                      Toleranz: ±
                      {(selectedOrder.items[currentWeighingIndex].quantity * 0.05).toFixed(3)}{' '}
                      {selectedOrder.items[currentWeighingIndex].unit}
                    </div>
                  </div>
                  <div className={`rounded-lg border p-4 ${weight === null ? 'bg-white border-gray-200' : isWithinTolerance ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`} data-id="element-1742">
                    <h5 className="font-medium text-gray-800 mb-2" data-id="element-1743">
                      Gemessenes Gewicht:
                    </h5>
                    <div className="text-3xl font-bold" data-id="element-1744">
                      {weight !== null ? `${weight} ${selectedOrder.items[currentWeighingIndex].unit}` : '-- ' + selectedOrder.items[currentWeighingIndex].unit}
                    </div>
                    {weight !== null && <div className={`text-sm mt-1 ${isWithinTolerance ? 'text-green-600' : 'text-red-600'}`} data-id="element-1745">
                        {isWithinTolerance ? 'Innerhalb der Toleranz' : 'Außerhalb der Toleranz!'}
                      </div>}
                  </div>
                </div>
                {/* Hinweise zum Gewürz */}
                <div className="mb-4" data-id="element-1746">
                  <h5 className="font-medium text-gray-800 mb-2 flex items-center" data-id="element-1747">
                    <InfoIcon size={18} className="mr-2 text-blue-600" data-id="element-1748" />
                    Hinweise zum Gewürz:
                  </h5>
                  <div className="bg-white rounded-lg border border-blue-200 p-3" data-id="element-1749">
                    <ul className="list-disc pl-5 space-y-1 text-gray-700" data-id="element-1750">
                      {selectedOrder.items[currentWeighingIndex].notes.map((note, index) => <li key={index} data-id="element-1751">{note}</li>)}
                    </ul>
                  </div>
                </div>
                {/* Großer Wiegen Button */}
                {weight === null ? <button onClick={startWeighing} disabled={isWeighing} className={`w-full py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium text-lg flex items-center justify-center gap-2 ${isWeighing ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1752">
                    <ScaleIcon size={24} data-id="element-1753" />
                    <span data-id="element-1754">{isWeighing ? 'Wiegen...' : 'Jetzt verwiegen'}</span>
                  </button> : isWithinTolerance ? <button onClick={handleConfirmWeighing} className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-lg flex items-center justify-center gap-2" data-id="element-1755">
                    <CheckCircleIcon size={24} data-id="element-1756" />
                    <span data-id="element-1757">Bestätigen & Weiter</span>
                  </button> : <div data-id="element-1758">
                    <div className="text-red-600 mb-2 text-center" data-id="element-1759">
                      Bitte korrigieren Sie die Menge!
                    </div>
                    <button onClick={startWeighing} className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium text-lg flex items-center justify-center gap-2" data-id="element-1760">
                      <ScaleIcon size={24} data-id="element-1761" />
                      <span data-id="element-1762">Erneut wiegen</span>
                    </button>
                  </div>}
              </div>
              {/* Wiegebereich */}
            </div>
          </div>
          {/* Fortschrittsanzeige */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1763">
            <div className="flex justify-between items-center mb-2" data-id="element-1764">
              <h5 className="font-medium text-gray-800" data-id="element-1765">
                Verwiegungsfortschritt:
              </h5>
              <span className="text-sm font-medium text-gray-600" data-id="element-1766">
                {weighingCompleted.length + (isWithinTolerance ? 1 : 0)} von{' '}
                {selectedOrder.items.length} Gewürzen
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-1767">
              <div className="bg-blue-500 h-2.5 rounded-full" style={{
            width: `${(weighingCompleted.length + (isWithinTolerance ? 1 : 0)) / selectedOrder.items.length * 100}%`
          }} data-id="element-1768"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-id="element-1769">
              {selectedOrder.items.map((item, index) => <div key={item.id} className={`p-2 rounded-lg border ${weighingCompleted.includes(item.id) ? 'bg-green-50 border-green-200' : index === currentWeighingIndex ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`} data-id="element-1770">
                  <div className="flex items-center" data-id="element-1771">
                    <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${weighingCompleted.includes(item.id) ? 'bg-green-500 text-white' : index === currentWeighingIndex ? 'bg-blue-500 text-white' : 'bg-gray-300'}`} data-id="element-1772">
                      {weighingCompleted.includes(item.id) && <CheckCircleIcon size={12} data-id="element-1773" />}
                    </div>
                    <div className="text-sm" data-id="element-1774">
                      {item.name} - {item.quantity} {item.unit}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {/* Detailansicht eines Gewürzes (Ansicht für frühere Implementierung) */}
      {view === 'detail' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1775">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-1776">
            <ArrowLeftIcon size={18} data-id="element-1777" />
            <span data-id="element-1778">Zurück zur Übersicht</span>
          </button>
          {selectedOrder && <>
              <div className="mb-6" data-id="element-1779">
                <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-1780">
                  {selectedOrder.recipeName}
                </h3>
                <div className="flex items-center gap-4" data-id="element-1781">
                  <p className="text-gray-600" data-id="element-1782">
                    Auftrag #{selectedOrder.orderId}
                  </p>
                  <div className="flex items-center text-gray-600" data-id="element-1783">
                    <ClipboardListIcon size={16} className="mr-1" data-id="element-1784" />
                    <span data-id="element-1785">
                      Fällig:{' '}
                      {selectedOrder.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                      Uhr
                    </span>
                  </div>
                </div>
              </div>
              {selectedItem ? <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6" data-id="element-1786">
                  <div className="flex justify-between items-start mb-6" data-id="element-1787">
                    <div className="flex items-center" data-id="element-1788">
                      <div className="p-3 bg-blue-200 rounded-full mr-4" data-id="element-1789">
                        <Leaf size={24} className="text-blue-700" data-id="element-1790" />
                      </div>
                      <div data-id="element-1791">
                        <h4 className="text-xl font-bold text-gray-800" data-id="element-1792">
                          {selectedItem.name}
                        </h4>
                        <p className="text-gray-600" data-id="element-1793">
                          Menge: {selectedItem.quantity} {selectedItem.unit}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-1794">
                          Artikel-Nr: {selectedItem.id}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedItem(null)} className="text-gray-500 hover:text-gray-700" data-id="element-1795">
                      <ArrowLeftIcon size={18} data-id="element-1796" />
                    </button>
                  </div>
                  {/* Hinweise mit Klick-Auswahl */}
                  <div className="mb-6" data-id="element-1797">
                    <h5 className="font-medium text-gray-800 mb-3 flex items-center" data-id="element-1798">
                      <AlertCircleIcon size={18} className="mr-2 text-blue-600" data-id="element-1799" />
                      Hinweise zum Gewürz (bitte auswählen)
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-1800">
                      {selectedItem.notes.map((note, index) => <div key={index} className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedNotes.includes(note) ? 'bg-blue-200 border-blue-300 text-blue-800' : 'bg-white border-gray-200 hover:bg-blue-50'}`} onClick={() => handleNoteToggle(note)} data-id="element-1801">
                          <div className="flex items-center" data-id="element-1802">
                            <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${selectedNotes.includes(note) ? 'bg-blue-500 text-white' : 'border border-gray-400'}`} data-id="element-1803">
                              {selectedNotes.includes(note) && <CheckCircleIcon size={12} data-id="element-1804" />}
                            </div>
                            <span className="text-sm" data-id="element-1805">{note}</span>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Verwiegungsprozess */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6" data-id="element-1806">
                    <h5 className="font-medium text-gray-800 mb-3" data-id="element-1807">
                      Verwiegung durchführen
                    </h5>
                    <div className="flex justify-center mb-4" data-id="element-1808">
                      <div className="bg-gray-100 rounded-lg p-6 w-64 text-center" data-id="element-1809">
                        <div className="text-3xl font-bold text-gray-800 mb-1" data-id="element-1810">
                          {selectedItem.quantity} {selectedItem.unit}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-1811">Zielgewicht</div>
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1812">
                      <button onClick={() => handleWeighingComplete(selectedItem.id)} className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium" data-id="element-1813">
                        Verwiegung bestätigen
                      </button>
                    </div>
                  </div>
                  {/* Ausgewählte Hinweise */}
                  {selectedNotes.length > 0 && <div className="bg-blue-100 rounded-lg p-4" data-id="element-1814">
                      <h5 className="font-medium text-blue-800 mb-2" data-id="element-1815">
                        Ausgewählte Hinweise:
                      </h5>
                      <ul className="list-disc pl-5 space-y-1" data-id="element-1816">
                        {selectedNotes.map((note, index) => <li key={index} className="text-blue-700" data-id="element-1817">
                            {note}
                          </li>)}
                      </ul>
                    </div>}
                </div> : <div data-id="element-1818">
                  <h4 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-1819">
                    Zu verwiegende Gewürze
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1820">
                    {selectedOrder.items.map(item => <div key={item.id} className={`border-2 rounded-lg p-6 ${item.status === 'completed' ? 'bg-blue-50 border-blue-200' : 'bg-blue-50 border-blue-200'} ${item.status !== 'completed' ? 'cursor-pointer hover:shadow-md' : ''}`} onClick={() => item.status !== 'completed' && handleItemSelect(item)} data-id="element-1821">
                        <div className="flex justify-between items-center" data-id="element-1822">
                          <div data-id="element-1823">
                            <div className="flex items-center" data-id="element-1824">
                              <div className="p-3 bg-blue-200 rounded-full mr-4" data-id="element-1825">
                                <Leaf size={24} className="text-blue-700" data-id="element-1826" />
                              </div>
                              <div data-id="element-1827">
                                <h5 className="text-lg font-semibold text-gray-900" data-id="element-1828">
                                  {item.name}
                                </h5>
                                <p className="text-base text-gray-600" data-id="element-1829">
                                  {item.quantity} {item.unit}
                                </p>
                                <p className="text-sm text-gray-500" data-id="element-1830">
                                  Artikel-Nr: {item.id}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div data-id="element-1831">
                            {item.status === 'completed' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-1832">
                                <CheckCircleIcon size={16} className="mr-1" data-id="element-1833" />
                                Erledigt
                              </span> : <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-base font-medium" onClick={e => {
                    e.stopPropagation();
                    handleItemSelect(item);
                  }} data-id="element-1834">
                                Verwiegen
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}
            </>}
        </div>}

      {/* Remaining Inventory View - New section */}
      {view === 'remainingInventory' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1835">
          <button onClick={() => setView('list')} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-1836">
            <ArrowLeftIcon size={18} data-id="element-1837" />
            <span data-id="element-1838">Zurück zur Übersicht</span>
          </button>
          {warehouseTransferComplete ? <div className="text-center py-8" data-id="element-1839">
              <div className="bg-green-100 p-4 rounded-full inline-flex mb-4" data-id="element-1840">
                <CheckCircleIcon size={48} className="text-green-600" data-id="element-1841" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1842">
                Übertragung erfolgreich!
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1843">
                Die Restbestände wurden erfolgreich an das Lager übermittelt.
              </p>
              <p className="text-sm text-gray-500" data-id="element-1844">
                Sie werden zur Übersicht zurückgeleitet...
              </p>
            </div> : <>
              <div className="mb-6" data-id="element-1845">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1846">
                  Restbestände für {selectedOrder.recipeName}
                </h3>
                <p className="text-gray-600" data-id="element-1847">
                  Die folgenden Gewürzrestbestände müssen ins Lager
                  zurückgeführt werden.
                </p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1848">
                <div className="flex items-center mb-4" data-id="element-1849">
                  <WarehouseIcon size={20} className="text-gray-700 mr-2" data-id="element-1850" />
                  <h4 className="font-medium text-gray-800" data-id="element-1851">
                    Restbestände für Lagereinlagerung
                  </h4>
                </div>
                <div className="overflow-hidden border border-gray-200 rounded-lg mb-6" data-id="element-1852">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-1853">
                    <thead className="bg-gray-50" data-id="element-1854">
                      <tr data-id="element-1855">
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1856">
                          Gewürz
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1857">
                          Artikel-Nr
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1858">
                          Restmenge
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-1859">
                      {remainingSpices.map(spice => <tr key={spice.id} data-id="element-1860">
                          <td className="px-4 py-4 whitespace-nowrap" data-id="element-1861">
                            <div className="flex items-center" data-id="element-1862">
                              <Leaf size={16} className="text-green-600 mr-2" data-id="element-1863" />
                              <span className="font-medium text-gray-900" data-id="element-1864">
                                {spice.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1865">
                            {spice.id}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right" data-id="element-1866">
                            <span className="font-medium text-gray-900" data-id="element-1867">
                              {spice.remaining} {spice.unit}
                            </span>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-1868">
                  <div className="flex items-start" data-id="element-1869">
                    <InfoIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1870" />
                    <div data-id="element-1871">
                      <h5 className="font-medium text-blue-800 mb-1" data-id="element-1872">
                        Lagerhinweis
                      </h5>
                      <p className="text-sm text-blue-700" data-id="element-1873">
                        Die Gewürzreste müssen unter Beachtung der spezifischen
                        Lagerhinweise eingelagert werden. Stellen Sie sicher,
                        dass alle Behälter ordnungsgemäß verschlossen und
                        etikettiert sind.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center" data-id="element-1874">
                  <button onClick={handleSendToWarehouse} disabled={isSendingToWarehouse} className={`py-3 px-6 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 
                      ${isSendingToWarehouse ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1875">
                    {isSendingToWarehouse ? <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" data-id="element-1876"></div>
                        <span data-id="element-1877">Übertrage an Lager...</span>
                      </> : <>
                        <SendIcon size={18} data-id="element-1878" />
                        <span data-id="element-1879">An Lager übermitteln</span>
                      </>}
                  </button>
                </div>
              </div>
            </>}
        </div>}

      {showPrintingPopup && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1880">
          <div className="bg-white rounded-xl p-8 max-w-md text-center" data-id="element-1881">
            <div className="bg-blue-100 p-3 rounded-full inline-flex mb-4" data-id="element-1882">
              <PrinterIcon size={48} className="text-blue-600" data-id="element-1883" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1884">
              Etikett wird gedruckt...
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-1885">
              Bitte warten Sie, während das Etikett für{' '}
              {selectedOrder?.items[currentWeighingIndex]?.name} gedruckt wird.
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1886">
              <div className="bg-blue-500 h-2 rounded-full animate-pulse" data-id="element-1887"></div>
            </div>
          </div>
        </div>}
    </div>;
}